import { clearNullDataDeep } from 'aa_common/front-end/helpers'
import { ASSET_CHANGE_SITUATION_TYPES, DATE_FORMAT_RFC } from 'common/constants'
import { useTryCallApi } from 'common/hooks/use-try-call-api'
import { FixedAssetsService } from 'common/open-api'
import moment from 'moment/moment'
import { useEffect, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { useDebouncedCallback } from 'use-debounce'

import { FIELD_NAMES } from '../validation'
import { useIsFullImpairment } from './use-is-full-impairment'

export const useGetDepreciation = (shouldGetData: boolean, ledgerSettingId?: number) => {
  const [depreciationRate, setDepreciationRate] = useState<number | undefined>()
  const isFullImpairment = useIsFullImpairment()
  const { watch } = useFormContext()
  const impairedValues = watch([
    FIELD_NAMES.impaired_at,
    FIELD_NAMES.impaired_amount,
    FIELD_NAMES.impair_residual_value,
    FIELD_NAMES.impair_service_life,
    FIELD_NAMES.impair_usage_period,
    FIELD_NAMES.impairment_method,
  ])
  const { biid } = useParams<{ biid: string }>()
  const { callApi: getDepreciationCall, data } = useTryCallApi(FixedAssetsService.planEventCreate)
  const debounceGetDepreciationCall = useDebouncedCallback(getDepreciationCall, 300)

  useEffect(() => {
    if (data?.data?.items?.length) {
      setDepreciationRate(data.data.items[0].impair_depreciation_rate)
    }
  }, [data])

  useEffect(() => {
    if (
      !shouldGetData ||
      isFullImpairment ||
      !biid ||
      !ledgerSettingId ||
      !impairedValues[FIELD_NAMES.impaired_at] ||
      !impairedValues[FIELD_NAMES.impaired_amount] ||
      !impairedValues[FIELD_NAMES.impairment_method]
    ) {
      return
    }

    debounceGetDepreciationCall(biid, {
      at: moment(impairedValues[FIELD_NAMES.impaired_at]).format(DATE_FORMAT_RFC),
      fixed_asset_ledgers: [
        clearNullDataDeep({
          change_situation_code: ASSET_CHANGE_SITUATION_TYPES.IMPAIRMENT,
          ledger_setting_id: ledgerSettingId,
          impaired_amount: impairedValues[FIELD_NAMES.impaired_amount],
          impair_residual_value: impairedValues[FIELD_NAMES.impair_residual_value],
          impair_service_life: impairedValues[FIELD_NAMES.impair_service_life],
          impair_usage_period: impairedValues[FIELD_NAMES.impair_usage_period],
          depreciation_ended_method: impairedValues[FIELD_NAMES.impairment_method],
        }),
      ],
    })
  }, [
    shouldGetData,
    isFullImpairment,
    biid,
    debounceGetDepreciationCall,
    impairedValues[FIELD_NAMES.impaired_at],
    impairedValues[FIELD_NAMES.impaired_amount],
    impairedValues[FIELD_NAMES.impair_residual_value],
    impairedValues[FIELD_NAMES.impair_service_life],
    impairedValues[FIELD_NAMES.impair_usage_period],
    impairedValues[FIELD_NAMES.impairment_method],
  ])

  return depreciationRate
}
